import {
  FC,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Column, Gauge, Funnel, Pie, ChartRefConfig } from '@ant-design/plots';
import { message } from 'antd';
import moment from 'moment';

import BubbleChartType from './components/bubble-chart/BubbleChart';
import ButtonsType from './components/ButtonsType';
import ChartLabel from './components/ChartLabel';
import { useModalState } from './ContextModal';
import { IChartData } from '../../../../../app/providers/redux/slices/reports/chart/types';
import { ITableDataGeneric, ITableState } from '../../../../../app/providers/redux/slices/reports/table/types';
import {
  convertYToNumber,
  endsWithNumber,
  getAverageSeries,
  getPieTooltip,
  getPlanSeries,
  handleCustomMetricGroup,
  handleEmptyDescription,
  isNumeric,
} from '../../../../../helpers/utils/functions';
import {
  appendDetails,
  buildSeriesForBubbleChart,
  calculateMaxMinBubbleChart,
  checkMetricsGroups,
  filterAxisShow,
  findRowRecurse,
  formatCellData,
  formatConvertData,
  formatMoneyData,
  getGroupDataForBarChart,
  predictDotsCount,
} from '../../../../../helpers/utils/reports';
import { showErrorMessage } from '../../../../../helpers/utils/ui';
import { unApi } from '../../../api/endpoints/reports/unApi';
import { groupMapping } from '../../../constants/reports';
import { useActions } from '../../../hooks/useActions';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import Loading from '../../loader/Loader';
import ChartWrap from '../ChartWrap';
import { getTitleYaxis } from '../cohort/components/utils';

type ChartGroups = {
  [key: string]: string[];
};
type Props = {
  blockId?: Props['isModal'] extends boolean ? undefined : string;
  date: string;
  isShow?: boolean;
  isModal?: true | undefined;
  blockName?: Props['isModal'] extends boolean ? undefined : string;
  metricsChecked?: ITableState['metricsChecked'];
  groupsChecked?: ITableState['groupsChecked'];
  selectedGroupings?: ITableState['groupings'];
  selectedMetrics?: ITableState['metrics'];
  chartType: string;
};

const getStylesForChart = (chartType: 'pie' | string) => {
  switch (chartType) {
    case 'pie':
      return { height: '84%' };
    default:
      return { height: '86%', paddingBottom: '20px' };
  }
};

const getUpdateSeries = (series: IChartData[], chartType: string) => {
  return series.map((item) => {
    const updatedItem = { ...item };
    if (typeof updatedItem.group === 'string') {
      updatedItem.group = updatedItem.group.replace(/<\/?[^>]+(>|$)/g, '');
      updatedItem.group = updatedItem.group.replace(/&nbsp;/g, ' ');
      if (chartType === 'bar') {
        updatedItem.group = updatedItem.group.replace(
          /\B(?=(\d{3})+(?!\d))/g,
          ''
        );
      }
    } else {
      updatedItem.group = '';
    }

    // Убираем неразрывные пробелы и span-теги
    updatedItem.group = updatedItem?.group
      ?.replace(/&nbsp;/g, ' ')
      ?.replace(/<span[^>]*>.*?<\/span>/gi, '');

    // Если тип графика "bar", форматируем group для числового отображения
    const regex = /\B(?=(\d{3})+(?!\d))/g;
    if (chartType === 'bar' && typeof updatedItem.group === 'string') {
      updatedItem.group = updatedItem.group.replace(regex, '');
    }

    // Преобразуем value в число или задаем 0 по умолчанию
    updatedItem.value =
      updatedItem.value != null && !isNaN(updatedItem.value)
        ? parseInt(String(updatedItem.value), 10)
        : 0;

    // Возвращаем обновленный объект

    updatedItem.value = parseInt(String(updatedItem.value), 10) || 0;

    return updatedItem;
  });
};

const getSortedSeries = (updatedSeries: any[], sortValue: 'ASC' | 'DESC') => {
  let newSortedSeries = [ ...updatedSeries ];
  if (sortValue === 'ASC') {
    newSortedSeries = newSortedSeries.sort((a, b) => a.value - b.value);
  } else if (sortValue === 'DESC') {
    newSortedSeries = newSortedSeries.sort((a, b) => b.value - a.value);
  }
  return newSortedSeries;
};

const colors = [ '#339dff', '#00b379', '#ffc233', '#d52f40', '#5d3faa' ];

const NoPlan = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '24px',
      fontWeight: 'bold',
      marginTop: '170px',
      textAlign: 'center',
    }}
  >
    Вы выбрали метрику, у которой не указан план
  </div>
);

const SelectPlan = () => (<div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '24px',
      fontWeight: 'bold',
      marginTop: '170px',
      textAlign: 'center',
    }}
  >
    Выберите метрику и укажите план
  </div>
);

const getColoredSeries = (updatedSeries: Record<PropertyKey, unknown>[], colorMap: any) => {
    const assignedColors = new Map();
    let localCounter = 0;
    return updatedSeries.map((item) => {
      if (!colorMap.current.has(item.group)) {
        // Выбираем цвет циклически, но для первого элемента фиксируем #339dff
        const nextColorIndex =
          localCounter === 0 ? 0 : localCounter % colors.length;
        colorMap.current.set(item.group, colors[nextColorIndex]);
        assignedColors.set(item.group, colors[nextColorIndex]); // Запоминаем цвет
        localCounter++; // Увеличиваем счетчик
      }
      return { ...item, color: colorMap.current.get(item.group) };
    });
  };
export const ChartUniversal: FC<Props> = (props) => {
  const prevReqDataRef = useRef({ isLoad: false, data: '' });
  const prevTableDataRef = useRef({ isLoad: false, data: '' });
  const chartRef = useRef<ChartRefConfig | undefined>(undefined);
  const {
    blockId,
    date,
    isShow = false,
    isModal = false,
    blockName,
    chartType,
    metricsChecked: blockMetricsChecked,
    groupsChecked: blockGroupsChecked,
    selectedGroupings,
    selectedMetrics
  } = props;
  const period = useTypedSelector((state) => state.period);
  const details = useTypedSelector(
    (state) => blockId && !isModal ? (state.details.detailsDashboard === 'blocks' ? state.details.detailsDashboardBlocks[blockId] : state.details.detailsDashboard) : state.details.details
  );

  const { isAllBlocksFetching, isModalChartFething } = useTypedSelector((state) => state.meta);
  const { filters } = useTypedSelector((state) =>
    blockId && !isModal ? state.filter.blocks[blockId] ?? { filters: [] } : state.filter.dashboardModal
  );
  const filtersStorageValue =
    JSON.parse(localStorage.getItem('dashboardFilters') || '') || [];

  const { filters: commonFilter } = useTypedSelector((state) =>
    blockId
      ? isShow
        ? state.filter
        : filtersStorageValue
      : filtersStorageValue
  );
  const commonFilters =
    commonFilter?.length === 0 && isShow ? filtersStorageValue : commonFilter;
  const { attributesDashboard, attributes, attributesDashboardBlocks } = useTypedSelector(
    (state) => state.attributes 
  );
  const currentAttributes = useMemo(() => {
    const attributesState = {
      attribution: null,
      type_applications: null,
    };
    if(isModal && !blockId) {
      attributesState.attribution = attributes.attribution;
      attributesState.type_applications = attributes.type_applications;
      return attributesState;
    }
    if(attributesDashboard.attribution[0] === 'blocks' && blockId) {
      attributesState.attribution = attributesDashboardBlocks[blockId].attribution;
    }else{
      attributesState.attribution = attributesDashboard.attribution;
    }
    if(attributesDashboard.type_applications[0] === 'blocks' && blockId) {
      attributesState.type_applications = attributesDashboardBlocks[blockId].type_applications;
    }else{
      attributesState.type_applications = attributesDashboard.type_applications;
    }
    return attributesState;
  }, blockId && !isModal? [ attributesDashboard, attributesDashboardBlocks[blockId] ] : [ attributes ]);
  const {
    groupings: storeGroupings,
    metrics: storeMetrics,
    totalChecked,
    metricsChecked: storeMetricsChecked,
    groupsChecked: storeGroupsChecked,
    tableData,
    percentages,
    sortTable,
  } = useTypedSelector((state) =>
    blockId && !isModal ? state.table.blocks[blockId] : state.table
  );
  const {     metricsChecked = [],
    groupsChecked = [], metrics = [], groupings = [] } = blockId && !isModal ? { metricsChecked: blockMetricsChecked,
      groupsChecked: blockGroupsChecked, metrics: selectedMetrics, groupings: selectedGroupings } : { metricsChecked: storeMetricsChecked,
        groupsChecked: storeGroupsChecked, metrics: storeMetrics, groupings: storeGroupings };
  const { series, cumulativeChart } = useTypedSelector((state) =>
    blockId && !isModal ? state.chart.blocks[blockId] : state.chart
  );

  const { modalState2 } = useModalState();
  const {
    isCompare,
    isChartLoading,
    currency,
    isChartCompareFetching,
    isChartFetching,
  } = useTypedSelector((state) =>
    blockId ? state.meta.blocks[blockId] : state.meta
  );
  const { compareFilters, comparePeriod } = useTypedSelector(
    (state) => state.compare
  );

  const {
    setSeries,
    setMetaChartLoading,
    setMetaChartFetching,
    setDetails,
    setDashboardBlockDetails,
    setMetaCompareChartFetching,
    setMetaModalChartFetching,
    setTableData,
    setAllBlocksFetching,
  } = useActions();
  const [ errorMessage, setErrorMessage ] = useState(
    'Нет данных, соответствующих условиям отчёта'
  );
  const [ yaxis, setYaxis ] = useState<any[]>([]);
  const [ chartGroups, setChartGroups ] = useState<ChartGroups>({
    firstGroup: [],
    secondGroup: [],
    thirdGroup: [],
    fourGroup: [],
    fifthGroup: [],
    sixthGroup: [],
    seventhGroup: [],
    eightGroup: [],
    ninethGroup: [],
    tenthGroup: [],
    eleventhGroup: [],
    twelfthGroup: [],
    thirteenth: [],
    fourteenth: [],
    customMetricGroup: [],
  });

  const seriesForBubbleChart = useMemo(
    () =>
      buildSeriesForBubbleChart(
        groupsChecked,
        totalChecked,
        chartType,
        tableData,
        metricsChecked
      ),
    [
      metricsChecked,
      chartType,
      groupsChecked,
      totalChecked,
      tableData,
      series,
      series.length,
    ]
  );

  const setDataChart = (tableData: any, data: any) => {
    try{
    if(prevTableDataRef.current.data === JSON.stringify(tableData) && chartType !== 'line' && chartType !== 'area' && !isModal) {
      return;
    }
    let newSeries: any = [];
    let newGroupsChecked = [ ...groupsChecked, ...totalChecked ];

    let metricFirstName = '';
    let sumSeries = 0;
    let newYaxis: any = [];
    switch (chartType) {
      case 'pie':
        if(!metricsChecked[0]?.dataKey) return;
        metricFirstName = metricsChecked[0].dataKey;

        newSeries = groupsChecked.map((group: any) => {
          const findRow = findRowRecurse(tableData.data, group?.pathStr);
          if (!findRow) {
            return {
              group: group.label,
              value: 0,
            };
          }

          const val = findRow?.[metricFirstName];
          return {
            group: group.label,
            value: Number(val),
          };
        });

        sumSeries = newSeries.reduce(
          (acc: number, item: any) => acc + item.value,
          0
        );

        const remainingValue =
          tableData?.total?.[metricFirstName] - sumSeries;
        newSeries = [
          {
            group: 'Остальные',
            value: Math.max(remainingValue, 0), // Убеждаемся, что значение не отрицательное
          },
          ...newSeries,
        ];
        setSeries(!blockId? newSeries : { data: newSeries, blockId });
        break;

      case 'tunnel':
        const colors = [ '#339dff', '#00b379', '#ffc233', '#d52f40', '#5d3faa' ];
        const metricColorMap = new Map();
        let colorIndex = 0;

        newSeries = [];

        const filteredGroups =
          metricsChecked.length === 1
            ? [ newGroupsChecked[0] ]
            : newGroupsChecked;

        metricsChecked.forEach((metric: any) => {
          let _label = metric.label;
          if (typeof metric.label === 'object') {
            _label = metric.label.props.children[0];
          }

          if (!metricColorMap.has(_label)) {
            metricColorMap.set(_label, colors[colorIndex % colors.length]);
            colorIndex++;
          }

          filteredGroups.forEach((group) => {
            let val = getGroupDataForBarChart(
              isCompare,
              tableData,
              percentages,
              metric,
              group
            );

            newSeries.push({
              key: group.label,
              metric: _label,
              value: val,
              color: metricColorMap.get(_label),
            });
          });
        });

        newSeries = newSeries.map((item: any) => ({
          ...item,
          key: item.key.replace(/&nbsp;/g, ' '),
        }));

        setSeries(!blockId? newSeries : { data: newSeries, blockId });
        break;
      case 'speedometer':
        newSeries = [];

        if (newGroupsChecked.length > 1) {
          newGroupsChecked = [ newGroupsChecked[0] ];
        }

        if (metricsChecked.length === 0) {
          console.warn('Нет доступных метрик для спидометра');
          break;
        }

        const selectedMetric = metricsChecked[0];
        const selectedGroup = newGroupsChecked[0];

        let _label =
          typeof selectedMetric.label === 'object'
            ? selectedMetric.label.props.children[0]
            : selectedMetric.label;

        let val = getGroupDataForBarChart(
          isCompare,
          tableData,
          percentages,
          selectedMetric,
          selectedGroup
        );

        newSeries.push({
          metric: _label,
          value: val,
        });

        metricsChecked.forEach((metric: any) => {
          newGroupsChecked.forEach((group) => {
            const metricLabel =
              metric && typeof metric.label === 'string'
                ? metric.label
                : 'Метрика';
            const groupLabel =
              group && typeof group.label === 'string' ? group.label : 'группа';
            const name = `${metricLabel} (${groupLabel})`;
            if (!data[group?.pathStr] || !data?.[group?.pathStr][metric.dataKey || metric.name])
              return;

            let dataResult = [
              ...(data?.[group?.pathStr][metric.dataKey || metric.name].map((item: any) => {
                return { ...item, category: name };
              }) ?? []),
            ];

            if (percentages.includes(metric.dataKey || metric.name)) {
              dataResult = dataResult.map((item, index) => {
                const y = parseFloat(
                  String((item.y / data.total[metric.dataKey || metric.name][index].y) * 100)
                ).toFixed(2);
                return { ...item, y };
              });
            }

            if (chartType === 'speedometer') {
              newSeries.push({
                name: name,
                data: dataResult,
                dataKey: metric.dataKey || metric.name,
                plan: metric?.plan,
                label: metric?.label,
                base_metric_name: metric?.base_metric_name,
                metric_formula: metric?.metric_formula,
              });
            } else {
              newSeries.push({ name, data: dataResult });
            }
          });
        });

        setSeries(!blockId? newSeries : { data: newSeries, blockId });
        break;

      case 'bar':
        metricsChecked.forEach((metric: any) => {
          let _label = metric.label;
          if (typeof metric.label === 'object') {
            _label = metric.label.props.children[0];
          }

          newGroupsChecked.forEach((group) => {
            let grp = group.label;
            let val = getGroupDataForBarChart(
              isCompare,
              tableData,
              percentages,
              metric,
              group
            );

            newSeries = [
              ...newSeries,
              {
                // key: isCompare ? _label.replace(" (2)", "") : _label,
                key: _label,
                group: grp,
                value: val,
              },
            ];
          });
        });
        newSeries = newSeries.map((item: any) => {
          let regex = /&nbsp;/g;
          const modifiedGroup = item.group?.replace(regex, ' ');
          return { ...item, group: modifiedGroup };
        });
        setSeries(!blockId? newSeries : { data: newSeries, blockId });
        break;

      default:
        // _series = []
        metricsChecked.forEach((metric: any) => {
          newGroupsChecked.forEach((group) => {
            const metricLabel =
              metric && typeof metric.label === 'string'
                ? metric.label
                : 'Метрика';
            const groupLabel =
              group && typeof group.label === 'string' ? group.label : 'группа';
            const name = `${metricLabel} (${groupLabel})`;
            if (!data?.[group?.pathStr] || !data?.[group?.pathStr]?.[metric?.dataKey || metric?.name]) return;

            let dataResult = [
              ...(data?.[group?.pathStr][metric.dataKey || metric?.name].map((item: any) => {
                return { ...item, category: name };
              }) ?? []),
            ];

            if (percentages.includes(metric.dataKey || metric?.name)) {
              dataResult = dataResult.map((item, index) => {
                const y = parseFloat(
                  String((item.y / data.total[metric.dataKey][index].y) * 100)
                ).toFixed(2);
                return { ...item, y };
              });
            }

              const _group = checkMetricsGroups(
                metric,
                name,
                group.label,
                metricsChecked
              );
              newYaxis.push({
                seriesName: _group.label,
                show: _group.show,
                opposite: true,
                isCustomMetric: (metric.dataKey || metric.name).startsWith('_'),
                dataKey: metric.dataKey || metric?.name,
              });
              newSeries.push({
                name: name,
                data: dataResult,
                dataKey: metric.dataKey || metric?.name,
                plan: metric?.plan,
                label: metric?.label,
                base_metric_name: metric?.base_metric_name,
                metric_formula: metric?.metric_formula,
              });
          });
        });
        if (isCompare) {
          setYaxis((prev) =>
            prev.map((el, index) => {
              if (index === 0) {
                return { ...el, show: true };
              }
              return { ...el, show: false };
            })
          );
        } else {
          const minNegativeValue = Math.min(
            ...newSeries.flatMap(({ data }: any) => data.map((el: any) => el.y))
          );
          let maxForMinNegativeValue = Number.NEGATIVE_INFINITY;
          if (minNegativeValue < 0) {
            newSeries.forEach(({ data }: any) => {
              const minInSeries = Math.min(...data.map((el: any) => el.y));
              if (minInSeries === minNegativeValue) {
                const maxInSeries = Math.max(...data.map((el: any) => el.y));
                maxForMinNegativeValue = Math.max(
                  maxForMinNegativeValue,
                  maxInSeries
                );
              }
            });
          }
          let rate = minNegativeValue / maxForMinNegativeValue;

          const maxValue = Math.max(
            ...newSeries.flatMap(({ data }: any) =>
              data.map((el: any) => parseInt(el.y))
            )
          );

          const findLargestYForDataKey = (
            seriesName: string,
            newSeries: any
          ) => {
            const series: any = newSeries.find(
              (item: any) => item.name === seriesName
            );
            if (series) {
              const { dataKey } = series;
              const groupToUpdate = dataKey.startsWith('_')
                ? handleCustomMetricGroup(series, cumulativeChart)
                : Object.keys(groupMapping).find((groupName) => {
                    const cleanedDataKey = endsWithNumber(dataKey)
                      ? dataKey.slice(0, dataKey.length - 2)
                      : dataKey;
                    return !cumulativeChart
                      ? groupMapping[groupName].includes(cleanedDataKey)
                      : groupMapping[groupName].includes(
                          dataKey.replace(/_cumulative/g, '')
                        );
                  });

              if (groupToUpdate) {
                const filteredSeries = newSeries.filter(
                  (item: any) => item.dataKey === dataKey
                );
                let largestY: any = null;

                filteredSeries.forEach((item: any) => {
                  const largestYInData = Math.max(
                    ...item.data.map((entry: any) => Number(entry.y))
                  );
                  if (largestY === null || largestYInData > largestY) {
                    largestY = largestYInData;
                  }
                });

                const cleanedSeriesName = seriesName
                  .replace(/\([^)]+\)/g, '')
                  .trim();
                setChartGroups((prev) => {
                  const updatedGroup = Array.isArray(prev[groupToUpdate])
                    ? [ ...prev[groupToUpdate] ]
                    : [];
                  if (!updatedGroup.includes(cleanedSeriesName)) {
                    updatedGroup.push(cleanedSeriesName);
                  }
                  return {
                    ...prev,
                    [groupToUpdate]: updatedGroup,
                  };
                });

                return largestY;
              }
            }
          };

          const yaxisesArr = filterAxisShow(newYaxis).map((axis: any) => {
            const largestY = findLargestYForDataKey(axis.seriesName, newSeries);

            const isShow = () => {
              const cleanedSeriesName = axis.seriesName
                .replace(/\([^)]+\)/g, '')
                .trim();

              const test = Object.values(chartGroups).flatMap((group) => {
                if (group?.includes(cleanedSeriesName)) {
                  return group?.filter(
                    (seriesName) => seriesName !== axis.seriesName
                  );
                }
              });
              return cleanedSeriesName === test.filter(Boolean)[0];
            };
            if (largestY) {
              const minValue = minNegativeValue < 0 ? rate * largestY * 1.2 : 0;
              return {
                ...axis,
                labels: {
                  offsetX: 0,
                  formatter: (valueInitial: any) => {
                    let value = +valueInitial;
                    if (isCompare) {
                      return value;
                    }
                    let roundedValue;

                    if (value <= 100) {
                      roundedValue = (Math.ceil(value * 1.01) / 10) * 10;
                    } else if (value <= 1000) {
                      roundedValue = (Math.ceil(value * 1.01) / 10) * 10;
                    } else if (value <= 10000) {
                      roundedValue = (Math.ceil(value * 1.01) / 1000) * 1000;
                    } else {
                      roundedValue = (Math.ceil(value * 1.01) / 10000) * 10000;
                    }
                    return formatCellData(roundedValue.toFixed());
                  },
                },

                max: largestY,
                min: isNaN(minValue) ? 0 : minValue,
                tickAmount: 6,
              };
            }
            const keywordsToCheck = [ 'Доля', 'CR', 'CTR' ];
            if (
              keywordsToCheck.some((keyword) =>
                axis.seriesName.includes(keyword)
              )
            ) {
              return {
                ...axis,
                labels: {
                  formatter: (value: any) => {
                    if (isCompare) {
                      return value;
                    }

                    return formatCellData(value.toFixed());
                  },
                },

                max: 100,
                min: 0,
                tickAmount: 6,
              };
            }
            const minValue = minNegativeValue < 0 ? rate * largestY : 0;
            return {
              ...axis,
              labels: {
                formatter: (value: any) => {
                  if (isCompare) {
                    return value;
                  }

                  return formatCellData(value.toFixed());
                },
              },

              show: isShow(),
              max: maxValue,
              min: isNaN(minValue) ? 0 : minValue,
              tickAmount: 6,
            };
          });
          const seenSeriesNames: any = {};

          yaxisesArr.forEach((item: any) => {
            if (item.show && seenSeriesNames[item.seriesName]) {
              item.show = false;
            } else {
              seenSeriesNames[item.seriesName] = true;
            }
          });
          setYaxis(yaxisesArr);

          if (groupings.length > 1) {
            setYaxis((prev) => [
              ...prev,
              {
                seriesName: `${
                  metricsChecked[0] &&
                  typeof metricsChecked[0].label === 'object'
                    ? ''
                    : metricsChecked[0]?.label ?? ''
                } (Итого и среднее выбранных)`,
                labels: {
                  formatter: (value: any) => {
                    if (isCompare) {
                      return value;
                    }

                    return formatCellData(value);
                  },
                },
                tickAmount: 6,
                show: false,
              },
            ]);
            tableData?.totalChosen?.active?.length > 0
              ? newSeries.unshift(getAverageSeries(newSeries, metricsChecked))
              : null;
          } else if (groupings.length === 1) {
            metricsChecked.forEach((metric) => {
              const series = newSeries.filter(
                (el: { name: string; dataKey: string }) =>
                  el.dataKey === (metric.dataKey || metric.name) &&
                  !el.name.includes('(Итого и среднее)')
              );
              const seriesNameArr = [ metric ];
              if (seriesNameArr.length === 1) {
                setYaxis((prev) => [
                  ...prev,
                  {
                    seriesName: `${seriesNameArr[0].label} (Итого и среднее выбранных)`,
                    labels: {
                      formatter: (value: any) => {
                        if (isCompare) {
                          return value;
                        }

                        return formatCellData(value);
                      },
                    },
                    tickAmount: 6,
                    show: false,
                  },
                ]);
              }

              tableData?.totalChosen?.active?.length > 0
                ? newSeries.unshift(getAverageSeries(series, seriesNameArr))
                : null;
            });
          }
          let planSeries: any[] = [];
          newSeries.forEach((el: any) => {
            if (isNumeric(el?.plan)) {
              const plan = getPlanSeries(
                el,
                details,
                el?.dataKey.endsWith('cumulative')
              );
              planSeries.push(plan);
            }
          });
          newSeries = newSeries.filter(
            (el: any, index: number, self: any[]) =>
              index ===
              self.findIndex(
                (t) => t.dataKey === el.dataKey && t.name === el.name
              )
          );
          planSeries = planSeries.filter(
            (el: any, index: number, self: any[]) =>
              index === self.findIndex((t) => t.dataKey === el.dataKey)
          );
          setSeries(!blockId? convertYToNumber([ ...newSeries, ...planSeries ]) : {
            data: convertYToNumber([ ...newSeries, ...planSeries ]),
            blockId,
          });
          break;
        }
    }
  }finally{
    prevTableDataRef.current.data = JSON.stringify(tableData);
    blockId && setAllBlocksFetching({ value: false, blockId });
    blockId && setMetaCompareChartFetching({ value: false, blockId });
    isModal && setMetaModalChartFetching({ value: false });
    setMetaChartLoading({ value: false, blockId });
  }
  };

  const fetchCompareData = () => {
    const time = Date.now();

    setMetaChartLoading({ value: true, blockId });

    const { compareFilters1, compareFilters2 } = compareFilters;
    const { comparePeriod1, comparePeriod2 } = comparePeriod;

    const format = 'YYYY-MM-DD';
    const groupings_names = groupings.map((col: any) => col.dataKey);
    let selected_groups_path = groupsChecked.map((item: any) => item.ids);
    selected_groups_path = selected_groups_path.filter(
      (el) => !el.includes('total')
    );
    selected_groups_path.push([ 'total' ]);
    const selected_metrics_names = metricsChecked.map((item: any) => item.dataKey);

    const _comparePeriod1 = {
      start: moment(comparePeriod1.start).format(format),
      end: moment(comparePeriod1.end).format(format),
    };
    const _comparePeriod2 = {
      start: moment(comparePeriod2.start).format(format),
      end: moment(comparePeriod2.end).format(format),
    };
    
    const reqData = {
      comparePeriod1: _comparePeriod1,
      comparePeriod2: _comparePeriod2,
      compareFilters1: compareFilters1,
      compareFilters2: compareFilters2,
      attribution: currentAttributes,
      details: details,
      groupings_names,
      selected_groups_path,
      selected_metrics_names,
    };

    if(prevReqDataRef.current.data === JSON.stringify({ ...reqData, chartType }) && prevReqDataRef.current.isLoad) {
      return;
    }else if(prevReqDataRef.current.data === JSON.stringify({ ...reqData, chartType })) {
      isModal && setMetaModalChartFetching({ value: false });
      setMetaChartFetching({ value: false, blockId });
      setMetaChartLoading({ value: false, blockId });
      return;
    }

    const dotsCount = predictDotsCount(
      reqData.comparePeriod1,
      reqData.selected_groups_path.length,
      reqData.selected_metrics_names.length,
      reqData.details as string
    );

    if (dotsCount > 1000) {
      if (dotsCount > 3000) {
        const newDetails = 'm';
        reqData.details = newDetails;
        isModal ? setDetails(newDetails) : blockId && setDashboardBlockDetails({ blockId, detail: newDetails });
      } else {
        const newDetails = appendDetails(reqData.details as string);
        reqData.details = newDetails;
        isModal ? setDetails(newDetails) : blockId && setDashboardBlockDetails({ blockId, detail: newDetails });
      }
      message.info(`Детализация для графика ${blockName ?? ''} изменена ввиду большого кол-ва данных`, 5);
    }

    unApi
      .getUniversalReportChartData(reqData, location.pathname)
      .then(function (response) {
        let data = response.data;
        if (data.success) {
            setDataChart({},data.data);
        }

        setErrorMessage('Нет данных, соответствующих условиям отчёта');
        if (response.status === 504) {
          setErrorMessage(
            'Запрос выполняется слишком долго, из-за большого количества данных. Можно уменьшить период отчёта, удалить не нужные метрики или фильтры.'
          );
        }
      })
      .finally(() => {
        prevReqDataRef.current = { isLoad: false, data: JSON.stringify({ ...reqData, chartType }) };
      });
  };

  const fetchData = () => {
    const time = Date.now();

    setMetaChartLoading({ value: true, blockId });

    const format = 'YYYY-MM-DD';
    const _period = {
      start: moment(period.start).format(format),
      end: moment(period.end).format(format),
    };

    const groupings_names = groupings.map((col: any) => col.dataKey);
    let selected_groups_path = groupsChecked.map((item: any) => item.ids);
    selected_groups_path = selected_groups_path.filter(
      (el) => !el.includes('total')
    );
    selected_groups_path.push([ 'total' ]);
    const selected_metrics_names = metricsChecked.map((item: any) => item.dataKey);

    if (chartType === 'bubble') {
      setMetaChartLoading({ value: false, blockId });
      return;
    }

    let reqData = {
      period: _period,
      details,
      filters: [ ...(filters ?? []), ...(isShow ? commonFilters : []) ],
      attribution: currentAttributes,
      groupings_names,
      selected_groups_path,
      selected_metrics_names,
    };

        //Избегаем лишних запросов, если data не изменилась
    if(prevReqDataRef.current.data === JSON.stringify({ ...reqData, chartType }) && prevReqDataRef.current.isLoad) {
      return;
    }else if(prevReqDataRef.current.data === JSON.stringify({ ...reqData, chartType })) {
      isModal && setMetaModalChartFetching({ value: false });
      setMetaChartFetching({ value: false, blockId });
      setMetaChartLoading({ value: false, blockId });
      return;
    }
    prevReqDataRef.current.isLoad = true;

    const dotsCount = predictDotsCount(
      reqData.period,
      reqData.selected_groups_path.length,
      reqData.selected_metrics_names.length,
      reqData.details as string
    );

    if (dotsCount > 1000) {
      if (dotsCount > 3000) {
        const newDetails = 'm';
        reqData.details = newDetails;
        isModal ? setDetails(newDetails) : blockId && setDashboardBlockDetails({ blockId, detail: newDetails });
      } else {
        const newDetails = appendDetails(reqData.details as string);
        reqData.details = newDetails;
        isModal ? setDetails(newDetails) : blockId && setDashboardBlockDetails({ blockId, detail: newDetails });
      }
      message.info(`Детализация для графика ${blockName ?? ''} изменена ввиду большого кол-ва данных`, 5);
    }

    unApi
      .getUniversalReportChartData(reqData, location.pathname)
      .then(function (response) {
        let data = response.data;
        if (data.success) {
            setDataChart({},data.data);
        }

        setErrorMessage('Нет данных, соответствующих условиям отчёта');
        if (response.status === 504) {
          setErrorMessage(
            'Запрос выполняется слишком долго, из-за большого количества данных. Можно уменьшить период отчёта, удалить не нужные метрики или фильтры.'
          );
        }
      })
      .catch(function (error) {
        if (error.response) {
          if (error.response.status === 504) {
            setErrorMessage(
              'Запрос выполняется слишком долго, из-за большого количества данных. \n Можно уменьшить период отчёта, удалить не нужные метрики или фильтры.'
            );
          }
        }
      })
      .finally(() => {
        prevReqDataRef.current = { isLoad: false, data: JSON.stringify({ ...reqData, chartType }) };
        blockId && setAllBlocksFetching({ value: false, blockId });
        blockId && setMetaChartFetching({ value: false, blockId });({ value: false, blockId });
        isModal && setMetaModalChartFetching({ value: false });
        setMetaChartLoading({ value: false, blockId });
      });
  };
  const fetchChartData = () => {
    if((isAllBlocksFetching && !isModal) || (isModal) || (!isChartFetching && !isModal) || (!isChartCompareFetching && !isModal)) {
        switch(chartType) {
            case 'speedometer':
            case 'bar':
            case 'tunnel':
            case 'pie':{
                loadRows();
                break;
            }
            default: {
                if (isCompare) {
                  fetchCompareData();
                } else {
                  fetchData();
                }
            }
        }
  }
  };
  const loadRows = (loadCurrent?: any) => {
    if(isModal){
      setDataChart(tableData,{});
      return;
    }
    setMetaChartLoading({ value: true, blockId });
    const format = 'YYYY-MM-DD';
    const _period = {
      start: moment(period.start).format(format),
      end: moment(period.end).format(format),
    };
    const groupings_names = groupings.map((col: any) => col.dataKey);
    const metrics_names = metrics.map((col) => col.dataKey);
    const expanded_ids = loadCurrent ? loadCurrent.clientData.path : [];

    let data = {
      period: _period,
      filters: [ ...filters, ...(isShow ? commonFilters : []) ],
      attribution: currentAttributes,
      groupings_names,
      metrics_names,
      expanded_ids,
    };

    window.localStorage.setItem('prevRequestState', JSON.stringify(data));
    unApi
      .getUniversalReportTableData(data, location.pathname)
      .then((response) => {
        const rowsWithIdAsString = response.data?.rows?.map((row: any) => {
          if (row.id) {
            return { ...row, id: row.id.toString() };
          }
          return row;
        });
        
        const data = { ...response.data, rows: rowsWithIdAsString };
        
        if (data.success) {
            setTableData({ ...data, groupings, blockId });
        } else {
          if (response.status === 504) {
            showErrorMessage(
              'Запрос выполняется слишком долго, из-за большого количества данных. \n Можно уменьшить период отчёта, удалить ненужные метрики или фильтры.'
            );
          }
        }
      })
      .catch((error) => {
        if (error?.response?.status !== 401) {
          showErrorMessage('Ошибка при получении данных');
        }
        if (error.response) {
          if (error.response.status === 504) {
            showErrorMessage(
              'Запрос выполняется слишком долго, из-за большого количества данных. \n Можно уменьшить период отчёта, удалить ненужные метрики или фильтры.'
            );
          }
        }
      }).finally(() => {
        blockId && setAllBlocksFetching({ value: false, blockId });
        blockId && setMetaChartFetching({ value: false, blockId });({ value: false, blockId });
        isModal && setMetaModalChartFetching({ value: false });
        setMetaChartLoading({ value: false, blockId });
      });
  };

  useEffect(() => {
    fetchChartData();
  }, [
    isChartFetching,
    isChartCompareFetching,
    isModal? isModalChartFething : null,
    !isModal? isAllBlocksFetching : null,
    chartType
  ]);

  useEffect(() => {
    if(chartType !== 'line'){
        setDataChart(tableData,{});
    }
  }, [ tableData ]);

  const colorMap = useRef(new Map()); // Храним цвета для элементов
  const globalCounter = useRef(0); // Глобальный счетчик для цвета (не сбрасывается!)

  const compareTooltipFormatter = (
    value: number,
    dataRowIndex: number,
    seriesIndex: any,
    series: any
  ) => {
    let _value = value;

    const seriesName = series[seriesIndex].name;
    const compareSeries = series.find((item: any) => item.name === seriesName);

    if (compareSeries) {
      if (typeof compareSeries.data[0] === 'number') {
        _value = compareSeries.data[0];
      } else {
        const _currentDate = compareSeries.data[dataRowIndex];

        if (_currentDate) {
          _value = _currentDate.x;
        }
      }
    }

    return _value;
  };

  const customTooltip = ({ seriesIndex, dataPointIndex }: any) => {
    const value = series[seriesIndex].data[dataPointIndex].y;
    const seriesName = series[seriesIndex].name;

    if (isCompare) {
      return `
                <div class="arrow_box">
                    <span> ${compareTooltipFormatter(
                      value,
                      dataPointIndex,
                      seriesIndex,
                      series
                    )} </span>
                    <span> 
                        ${seriesName}: ${value}
                    </span>
                </div>
            `;
    } else {
      const seriesName = yaxis[seriesIndex].seriesName.toLowerCase();
      let data = formatCellData(value);

      if (seriesName.includes('сумма')) {
        data = formatMoneyData(value, currency);
      }

      if (seriesName.includes('конверсия')) {
        data = formatConvertData(value);
      }

      return `
                <div class="arrow_box">
                    <span> ${compareTooltipFormatter(
                      value,
                      dataPointIndex,
                      seriesIndex,
                      series
                    )} </span>
                    <span> ${seriesName}: ${data} </span>
                </div>
            `;
    }
  };

  const bubbleTooltip = (title: string, items: any[]) => {
    let seriesData;
    let groupName;

    try {
      seriesData = items;
      groupName = items[2].value;
    } catch (e) {
      seriesData = null;
    }

    if (seriesData) {
      return `
                <div class="arrow_box">
                    <b> ${groupName} </b>
                    <hr />
                    <span> ${metricsChecked[0].label}: ${seriesData[0].value} </span>
                    <span> ${metricsChecked[1].label}: ${seriesData[1].value} </span>
                    <span> ${metricsChecked[2].label}: ${seriesData[3].value} </span>
                </div>
            `;
    } else {
      return `
                <div class="arrow_box">
                    <span> Нет данных </span>
                </div>
            `;
    }
  };

  const offsetsBubbleChart = useMemo(
    () => calculateMaxMinBubbleChart(seriesForBubbleChart),
    [ seriesForBubbleChart ]
  );

  const lineChartTooltip = {
    enabled: true,
    followCursor: false,
    shared: true,
    intersect: false,
    custom: customTooltip,
  };

  const otherChartTooltip = {
    enabled: true,
    y: {
      formatter: (value: any) => {
        return formatCellData(value);
      },
    },
  };

  const ChooseChartType = useCallback(() => {
    const generalOptions = {
      chart: {
        animations: {
          enabled: false,
          easing: 'linear',
          dynamicAnimation: { speed: 1000 },
        },
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      dataLabels: { enabled: false },
      legend: { show: false },
      noData: {
        text: handleEmptyDescription(
          metricsChecked,
          groupsChecked,
          chartType
        ),
      },
      xaxis: { type: 'category' },
      annotations: { yaxis: [ { y: 0, borderColor: '#262626' } ] },
      stroke: {
        width: 1.7,
        curve: 'smooth',
      },
    };
    const updatedSeries = getUpdateSeries(series, chartType);
    const sortValue = Object.values(sortTable)[0] as 'ASC' | 'DESC';
    const sortedSeries = getSortedSeries(updatedSeries, sortValue);

    const uniqueYaxis = useMemo(() => {
      const axisTitles = yaxis.map((axis) => getTitleYaxis(chartGroups, axis));
      return [ ...new Set(axisTitles) ];
    }, [ yaxis, chartGroups ]);

    const transformedSeries = [ ...(updatedSeries || []) ]
      .filter((item) => item && item.value !== undefined) // Убираем undefined/null элементы
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0)) // Предотвращаем ошибку
      .slice(0, 5);

    const otherValue = updatedSeries
      .slice(5) // Берем оставшиеся
      .reduce((sum, item) => sum + item.value, 0); // Складываем их значения

    if (otherValue > 0) {
      transformedSeries.push({ group: 'Другие', value: otherValue });
    }

    const config = {
      data: updatedSeries,
      xField: 'metric', // Теперь X - это метрики
      yField: 'value', // Y - значения метрик
      seriesField: 'key', // Группы - это отдельные категории внутри метрики
      isGroup: true, // Разделяем по группам внутри метрики
      isStack: false, // Оставляем столбцы раздельными
      label: {
        style: {
          fill: '#000',
          opacity: 1,
        },
      },

      tooltip: {
        formatter: (datum: any) => ({
          name: datum.key,
          value: `${formatCellData(datum.value)}`,
        }),
      },

      xAxis: {
        label: {
          autoHide: false,
          autoRotate: true,
        },
      },
    };
    let sum = 0;
    updatedSeries.forEach((el) => {
      if (el.plan !== undefined) {
        sum = el.plan;
      }
    });
    const planPerDay = Number(modalState2.plan) || 1;
    const reportPeriodDays = sum * sum;
    const maxGaugeValue = planPerDay * reportPeriodDays;

    const metricValue = updatedSeries.reduce(
      (acc: number, item: any) => acc + item.value,
      0
    );
    const percentCompleted = Math.round((metricValue / maxGaugeValue) * 100);

    const config2 = {
      width: 420,
      height: 420,
      legend: false,
      percent: percentCompleted / 100,
      statistic: {
        content: {
          style: {
            fontSize: '24px',
            fontWeight: 'bold',
            fill: '#333',
          },
          formatter: () => `${percentCompleted}%`,
        },
      },
    };
    const selectedMetric = metricsChecked[0];

    const noPlanError =
      selectedMetric && !selectedMetric.plan ? <NoPlan /> : null;

    const errorPlan =
      isNaN(percentCompleted) ||
      percentCompleted === Infinity ||
      percentCompleted === -Infinity ? (
        <SelectPlan/>
      ) : null;

    const speedometerComponent =
      noPlanError ||
      errorPlan ||
      (updatedSeries.length > 0 ? (
        <Gauge {...config2} animation={true} />
      ) : <SelectPlan/> 
    );

    const finalSpeedometer = speedometerComponent ?? <></>;

    const isNullableData = updatedSeries?.every((item) => !item.value);

    const variantChart: { [key: string]: React.ReactElement } = {
      pie: updatedSeries.length && !isNullableData? (
        <div style={{ position: 'relative' }}>
          <Pie
            chartRef={chartRef.current}
            renderer='svg'
            animation={false}
            data={updatedSeries}
            
            angleField="value"
            colorField="group"
            color={updatedSeries.map((item, index) => {
              if (item.group === 'Остальные') {
                return '#6E6E6E'; // Более темный серый цвет для "Остальных"
              }
              return getColoredSeries(updatedSeries, colorMap)[index]?.color || '#87CEFA'; // Если цвет найден в coloredSeries, используем его, иначе - стандартный цвет
            })}
            theme={{
              colors10: [ '#005DCA', '#4A90E2', '#87CEFA' ],
              colors20: [ '#005DCA', '#4A90E2', '#87CEFA' ],
            }}
            tooltip={{
              formatter: (datum) => ({
                name: datum.group,
                value: `${getPieTooltip(updatedSeries, datum.value)}`,
              }),
            }}
            label={false}
          />
        </div>
      ) : (
        <div style={{   display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400 }}>
          <span style={{ fontSize: 20 }}>{handleEmptyDescription(
          metricsChecked,
          groupsChecked,
          chartType)
        }</span>
       </div>
      ),

      line: (
        <div style={{ position: 'relative', height: 400 }}>
          {updatedSeries.length ? (<><ChartWrap
            type="line"
            animation={false}
            series={updatedSeries}
            options={{
              ...generalOptions,
              tooltip: isCompare ? lineChartTooltip : otherChartTooltip,
              yaxis,
            }}
            height={'100%'}
          />
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              transform: 'translateY(0)',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-end',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            {uniqueYaxis.map((axisTitle, index) => {
              const parts = axisTitle.split('|').map((part) => part.trim());

              return (
                <h1
                  key={index}
                  style={{
                    fontSize: '10px',
                    textAlign: 'end',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {parts.map((part, i) => {
                    if (!colorMap.current.has(part)) {
                      const nextColorIndex =
                        globalCounter.current % colors.length;
                      colorMap.current.set(part, colors[nextColorIndex]);
                      globalCounter.current++;
                    }

                    const color = colorMap.current.get(part);

                    return (
                      <Fragment key={i}>
                        {i > 0 && <span style={{ color: '#999' }}> | </span>}
                        <span style={{ color }}>{part}</span>
                      </Fragment>
                    );
                  })}
                </h1>
              );
            })}
          </div></>) : (
          <div style={{   display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 400 }}>
            <span style={{ fontSize: 20 }}>{handleEmptyDescription(
          metricsChecked,
          groupsChecked,
          chartType)}</span>
         </div>
        )}
        </div>
      ),
      area: (
        <ChartWrap
          type="area"
          series={sortedSeries}
          animation={false}
          options={{
            ...generalOptions,
            tooltip: otherChartTooltip,
            chart: { ...generalOptions.chart, stacked: true },
          }}
          height={isShow ? '100%' : 400}
        />
      ),
      speedometer: updatedSeries.length ? (
        finalSpeedometer
      ) : (
        <ChartWrap
          type="line"
          animation={false}
          series={updatedSeries}
          options={{
            ...generalOptions,
            tooltip: isCompare ? lineChartTooltip : otherChartTooltip,
            yaxis,
          }}
          height="100%"
        />
      ),

      tunnel: (
        <>
          <Funnel
            legend={false}
            {...config}
            animation={false}
            style={{
              height:
                updatedSeries.length && !isNullableData ? 'inherit' : '200px',
            }}
          />
          {updatedSeries.length && !isNullableData ? null : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: 20 }}>Нет данных</span>
            </div>
          )}
        </>
      ),
      bubble: (
        <BubbleChartType
          axisMinMax={offsetsBubbleChart}
          tooltip={bubbleTooltip}
          data={seriesForBubbleChart}
          _metricsChecked={metricsChecked}
        />
      ),
      bar: (
        <Column
          xAxis={{
            label: { autoHide: false, autoRotate: true },
          }}
          style={{ marginTop: '20px', height: isShow ? '100%' : 400 }}
          animation={false}
          xField={'group'}
          yField={'value'}
          seriesField={'key'}
          label={{
            position: 'middle',

            style: { fill: '#FFFFFF', opacity: 1 },
            content: (item) => item.value.toString().replace(RegExp, ' '),
          }}
          data={sortedSeries}
          tooltip={{
            formatter: (datum) => ({
              name: datum.key,
              value: `${formatCellData(datum.value)}`,
            }),
          }}
          legend={false}
          isGroup={true}
          isStack={false}
        />
      ),
    };

    return (
      <div key={chartType} style={getStylesForChart(chartType)}>
        {variantChart[chartType as keyof typeof variantChart]}
      </div>
    );
  }, [ chartType, series ]);

    return (
      <>
        <div
          className={`universal-chart ${isShow ? 'show' : ''} ${chartType}`}
          style={{ ...(isShow ? { minHeight: 'unset', height: '100%' } : { height: '100%' }) }}
          id="universal_chart"
        >
          <Loading loading={isChartLoading} hasTip fullHeight>
            <ButtonsType />
            <ChooseChartType />
            <ChartLabel chartFilter={date} chartType={series.length ? chartType : undefined} currentMetrics={metricsChecked} chartRef={chartRef}/>
          </Loading>
        </div>
      </>
    );
};

export default memo(ChartUniversal, (prev, next) => {
  return JSON.stringify(prev) === JSON.stringify(next);
});
